import React, {
    useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import Translator from '%app/js/common/services/translator';
import MaterialSymbol from '../MaterialSymbol';
import LabeledField from './LabeledField';
import useEventNameSuggestions, { MIN_QUERY_LENGTH } from './useEventNameSuggestions';
import {
    BLUR_CLOSE_DELAY_MS,
    INPUT_SX,
    ITEM_ACTIVE_SX,
    ITEM_BASE_SX,
    LIST_SX,
    PAPER_SX,
    POPPER_MIN_WIDTH,
    POPPER_SX,
    PRIMARY_TEXT_SX,
    SECONDARY_TEXT_SX,
    SHOW_ALL_ITEM_ACTIVE_SX,
    SHOW_ALL_ITEM_SX,
    SHOW_ALL_TEXT_SX,
    STATUS_SX,
} from './autocompleteStyles';
import { joinTicketMeta } from '../../services/ticketMeta';

function buildMeta(suggestion) {
    return joinTicketMeta([suggestion.dateStarts, suggestion.location]);
}

function WhatField({
    label, placeholder, value, onChange, onSelectSuggestion, onShowAllResults, hideLabel = false,
}) {
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const blurTimeoutRef = useRef(null);
    const optionRefs = useRef([]);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [popperWidth, setPopperWidth] = useState(POPPER_MIN_WIDTH);

    const { suggestions, hasMore, loading } = useEventNameSuggestions(value);
    const showAllIndex = hasMore ? suggestions.length : -1;
    const optionCount = suggestions.length + (hasMore ? 1 : 0);

    const showDropdown = open && (loading || suggestions.length > 0 || value.trim().length >= MIN_QUERY_LENGTH);

    useLayoutEffect(() => {
        if (showDropdown && wrapperRef.current) {
            const measured = wrapperRef.current.offsetWidth;
            setPopperWidth(Math.max(measured, POPPER_MIN_WIDTH));
        }
    }, [showDropdown]);

    const clearBlurTimeout = useCallback(() => {
        if (blurTimeoutRef.current !== null) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => clearBlurTimeout, [clearBlurTimeout]);

    useEffect(() => {
        const activeNode = optionRefs.current[activeIndex];
        if (activeNode && typeof activeNode.scrollIntoView === 'function') {
            activeNode.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex]);

    const handleChange = useCallback((event) => {
        onChange(event.target.value);
        setOpen(true);
        setActiveIndex(-1);
    }, [onChange]);

    const handleFocus = useCallback(() => {
        clearBlurTimeout();
        setOpen(true);
    }, [clearBlurTimeout]);

    const handleBlur = useCallback(() => {
        clearBlurTimeout();
        blurTimeoutRef.current = setTimeout(() => {
            blurTimeoutRef.current = null;
            setOpen(false);
        }, BLUR_CLOSE_DELAY_MS);
    }, [clearBlurTimeout]);

    const closeDropdownAndBlur = useCallback(() => {
        setOpen(false);
        setActiveIndex(-1);
        if (inputRef.current) {
            inputRef.current.blur();
        }
    }, []);

    const selectSuggestion = useCallback((suggestion) => {
        if (!suggestion || !suggestion.slug) {
            return;
        }
        onChange(suggestion.text);
        onSelectSuggestion(suggestion);
        closeDropdownAndBlur();
    }, [onChange, onSelectSuggestion, closeDropdownAndBlur]);

    const activateShowAllResults = useCallback(() => {
        onShowAllResults();
        closeDropdownAndBlur();
    }, [onShowAllResults, closeDropdownAndBlur]);

    const activateOption = useCallback((index) => {
        if (index === showAllIndex) {
            activateShowAllResults();
            return;
        }
        selectSuggestion(suggestions[index]);
    }, [activateShowAllResults, selectSuggestion, showAllIndex, suggestions]);

    const handleKeyDown = useCallback((event) => {
        if (!showDropdown || optionCount === 0) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex(index => (index + 1) % optionCount);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(index => (index <= 0 ? optionCount - 1 : index - 1));
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            activateOption(activeIndex);
        } else if (event.key === 'Escape') {
            setOpen(false);
            setActiveIndex(-1);
        }
    }, [activeIndex, activateOption, optionCount, showDropdown]);

    const renderDropdownBody = () => {
        if (loading) {
            return (
                <Box sx={STATUS_SX} data-testid="search-panel-what-loading">
                    {Translator.trans('search_panel.what.loading')}
                </Box>
            );
        }
        if (suggestions.length === 0) {
            return (
                <Box sx={STATUS_SX} data-testid="search-panel-what-empty">
                    {Translator.trans('search_panel.what.no_results')}
                </Box>
            );
        }
        return (
            <Box component="ul" sx={LIST_SX} role="listbox" data-testid="search-panel-what-list">
                {suggestions.map((suggestion, index) => {
                    const isActive = index === activeIndex;
                    const meta = buildMeta(suggestion);
                    return (
                        <Box
                            key={suggestion.id}
                            component="li"
                            role="option"
                            aria-selected={isActive}
                            ref={(node) => { optionRefs.current[index] = node; }}
                            sx={isActive ? { ...ITEM_BASE_SX, ...ITEM_ACTIVE_SX } : ITEM_BASE_SX}
                            data-testid={`search-panel-what-option-${suggestion.id}`}
                            onMouseDown={(event) => {
                                event.preventDefault();
                                selectSuggestion(suggestion);
                            }}
                            onMouseEnter={() => setActiveIndex(index)}
                        >
                            <Typography component="span" sx={PRIMARY_TEXT_SX}>{suggestion.text}</Typography>
                            {meta ? (
                                <Typography component="span" sx={SECONDARY_TEXT_SX}>{meta}</Typography>
                            ) : null}
                        </Box>
                    );
                })}
                {hasMore ? (
                    <Box
                        component="li"
                        role="option"
                        aria-selected={activeIndex === showAllIndex}
                        ref={(node) => { optionRefs.current[showAllIndex] = node; }}
                        sx={activeIndex === showAllIndex
                            ? { ...ITEM_BASE_SX, ...SHOW_ALL_ITEM_SX, ...SHOW_ALL_ITEM_ACTIVE_SX }
                            : { ...ITEM_BASE_SX, ...SHOW_ALL_ITEM_SX }}
                        data-testid="search-panel-what-show-all"
                        onMouseDown={(event) => {
                            event.preventDefault();
                            activateShowAllResults();
                        }}
                        onMouseEnter={() => setActiveIndex(showAllIndex)}
                    >
                        <Typography component="span" sx={SHOW_ALL_TEXT_SX}>
                            {Translator.trans('search_panel.what.show_all_results')}
                            <MaterialSymbol name="arrow_forward" size={16} />
                        </Typography>
                    </Box>
                ) : null}
            </Box>
        );
    };

    return (
        <Box ref={wrapperRef} sx={{ width: '100%', minWidth: 0 }}>
            <LabeledField
                label={label}
                hideLabel={hideLabel}
                icon={<SearchIcon fontSize="small" />}
                active={open}
                renderInput={({ id }) => (
                    <InputBase
                        id={id}
                        type="text"
                        value={value}
                        placeholder={placeholder}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        inputRef={inputRef}
                        inputProps={{
                            'data-testid': 'search-panel-what',
                            'aria-label': label,
                            'aria-autocomplete': 'list',
                            'aria-expanded': showDropdown,
                            autoComplete: 'off',
                        }}
                        sx={INPUT_SX}
                    />
                )}
            />
            <Popper
                open={showDropdown}
                anchorEl={wrapperRef.current}
                placement="bottom-start"
                sx={POPPER_SX}
                modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
            >
                <Paper sx={{ ...PAPER_SX, width: popperWidth }} data-testid="search-panel-what-dropdown">
                    {renderDropdownBody()}
                </Paper>
            </Popper>
        </Box>
    );
}

WhatField.propTypes = {
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelectSuggestion: PropTypes.func.isRequired,
    onShowAllResults: PropTypes.func.isRequired,
    hideLabel: PropTypes.bool,
};

export default WhatField;
